import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Plus, Bandage } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { KitCard } from "@/components/firstaid/KitCard";
import { KitDialog, AssignDialog, ChecklistDialog } from "@/components/firstaid/KitDialogs";

export const fmtDate = d => { try { return format(parseISO(d), "d MMM yyyy"); } catch { return d || "—"; } };

export default function FirstAidKits() {
  const location = useLocation();
  const [perms, setPerms] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [scope, setScope] = useState("mine");
  const [rows, setRows] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    api.get("/first-aid/permissions").then(r => { setPerms(r.data); if (r.data.can_manage) setScope("all"); }).catch(() => setPerms({ can_manage: false }));
    api.get("/first-aid/checklist").then(r => setChecklist(r.data)).catch(() => {});
    api.get("/teams").then(r => setTeams(r.data || [])).catch(() => {});
    setFocus(new URLSearchParams(location.search).get("kit"));
  }, [location.search]);
  useEffect(() => { if (perms?.can_manage) api.get("/chat/users").then(r => setUsers(r.data)).catch(() => {}); }, [perms]);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/first-aid/kits?scope=${scope}`); setRows(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load kits"); setRows([]); }
  }, [scope]);
  useEffect(() => { if (perms) load(); }, [perms, load]);

  const patch = (k) => setRows(rs => (rs || []).map(r => r.id === k.id ? k : r));
  const remove = async (k) => {
    if (!window.confirm(`Delete kit ${k.number}? This can't be undone.`)) return;
    try { await api.delete(`/admin/first-aid/kits/${k.id}`); toast.success("Kit deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unassign = async (k) => {
    try { const { data } = await api.post(`/admin/first-aid/kits/${k.id}/unassign`); patch(data); toast.success("Kit unassigned"); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const counts = { total: rows?.length || 0, awaiting: (rows || []).filter(r => r.status === "awaiting").length, restock: (rows || []).filter(r => r.restock_flag).length, stale: (rows || []).filter(r => r.is_stale).length };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="first-aid-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Welfare & safety</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">First Aid Kits</h1>
          <p className="opacity-60 text-sm mt-1">{perms?.can_manage ? "Register numbered kits, hand them to coaches and keep an eye on stock levels." : "Accept the kit you've collected, then keep its contents list up to date after every check."}</p>
        </div>
        {perms?.can_manage && (
          <div className="flex gap-2">
            <button type="button" onClick={() => setChecklistOpen(true)} data-testid="fak-checklist-btn" className="px-4 py-3 rounded-md border border-border text-[10px] tracking-widest uppercase hover:border-[#83B68B]">Checklist template</button>
            <button type="button" onClick={() => setEditing({})} data-testid="fak-new-btn" className="btn-volt px-5 py-3 rounded-md inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Register kit</button>
          </div>
        )}
      </div>

      {perms?.can_manage && (
        <div className="flex flex-wrap items-center gap-2" data-testid="fak-scope-tabs">
          {[{ k: "all", l: "All kits" }, { k: "mine", l: "My kits" }].map(t => (
            <button key={t.k} onClick={() => setScope(t.k)} data-testid={`fak-scope-${t.k}`} className={`px-4 py-2 rounded text-[10px] tracking-widest uppercase border ${scope === t.k ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`}>{t.l}</button>
          ))}
          {scope === "all" && rows && (
            <div className="ml-auto flex gap-3 text-[10px] tracking-widest uppercase opacity-70" data-testid="fak-summary">
              <span>{counts.total} kits</span>{counts.awaiting > 0 && <span className="text-amber-400">{counts.awaiting} awaiting acceptance</span>}{counts.restock > 0 && <span className="text-red-400">{counts.restock} need restock</span>}{counts.stale > 0 && <span className="text-amber-400">{counts.stale} overdue check</span>}
            </div>
          )}
        </div>
      )}

      {rows === null ? <div className="opacity-50 font-display tracking-widest">LOADING…</div>
        : rows.length === 0 ? (
          <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="fak-empty">
            <Bandage className="w-8 h-8 mx-auto mb-3 opacity-60" />
            {scope === "all" ? "No kits registered yet." : "No first aid kit has been assigned to you yet."}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="fak-list">
            {rows.map(k => (
              <KitCard key={k.id} kit={k} checklist={checklist} perms={perms} highlight={focus === k.id}
                onChanged={patch} onEdit={() => setEditing(k)} onAssign={() => setAssigning(k)} onUnassign={() => unassign(k)} onDelete={() => remove(k)} />
            ))}
          </div>
        )}

      <KitDialog kit={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <AssignDialog kit={assigning} users={users} teams={teams} onClose={() => setAssigning(null)} onSaved={(k) => { setAssigning(null); patch(k); }} />
      <ChecklistDialog open={checklistOpen} items={checklist} onClose={() => setChecklistOpen(false)} onSaved={(items) => { setChecklist(items); setChecklistOpen(false); }} />
    </div>
  );
}
